import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Crown, LayoutDashboard, Radio, Lock, ShieldCheck, Download, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StaffLoginModal } from "@/components/StaffLoginModal";
import { isBroadcaster } from "@/pages/BroadcasterPage";

const activeFor = (to, isActive) => isActive && !(to === "/football" && window.location.pathname.startsWith("/football/east-africa"));
const FOOTBALL = [["World football", "/football"], ["East Africa", "/football/east-africa"], ["Football highlights", "/highlights"]];
const LINKS = [
  ["Live Radio", "/radio"], ["Live TV", "/live"], ["Highlights", "/highlights"], ["Shows", "/vod"], ["Videos", "/videos"], ["Football", "/football", FOOTBALL],
  ["News", "/news"], ["Utilities", "/utilities"], ["Pay", "/pay"], ["Pricing", "/pricing"],
];
const MORE = [["Creators", "/creators"], ["Vote", "/vote"], ["Events", "/events"], ["Who we are", "/about"], ["Contact", "/contact"]];

const NavMenu = ({ items, label = "Football", id = "football" }) => {
  const [open, setOpen] = useState(false);
  const here = items.some(([, to]) => window.location.pathname.startsWith(to));
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(true)} data-testid={`nav-${id}`} aria-haspopup="menu" aria-expanded={open}
        className={`px-2.5 xl:px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${here ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-1 z-30" data-testid={`nav-${id}-menu`}>
          <div className="w-52 rounded-xl bg-[#121620] border border-[#273042] shadow-2xl py-1">
            {items.map(([label, to]) => (
              <NavLink key={to} to={to} end onClick={() => setOpen(false)} data-testid={`nav-${id}-${to.slice(1).replace(/\//g, "-")}`}
                className={({ isActive }) => `block px-4 py-2.5 text-sm transition-colors ${isActive ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>{label}</NavLink>))}
          </div>
        </div>
      )}
    </div>
  );
};

const LiveFootballBtn = () => {
  return (
    <Link to="/football" data-testid="header-live-football-btn"
      className="ml-2 mr-auto lg:mr-2 shrink-0 inline-flex items-center gap-1.5 h-8 px-2.5 min-[360px]:px-3 rounded-full bg-[#E11D2A]/15 border border-[#E11D2A]/50 text-white text-xs font-extrabold uppercase tracking-wide hover:bg-[#E11D2A]/30 transition-colors">
      <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /><span className="hidden min-[380px]:inline">Live</span><span className="hidden min-[340px]:inline">Football</span>
    </Link>
  );
};

export const Header = () => {
  const { user, isAuthed, isAdmin, isStaff, isSubscribed, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const nav = useNavigate();
  const openAuth = () => window.dispatchEvent(new Event("open-auth"));

  useEffect(() => {
    const open = () => setStaffOpen(true);
    window.addEventListener("open-staff-login", open);
    return () => window.removeEventListener("open-staff-login", open);
  }, []);

  return (
    <header className="sticky top-0 z-40 glass border-b border-[#273042]/80">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
          <img src="/bb-logo.png" alt="B&B KIGALI 89.7" className="h-10 w-auto object-contain" />
          <span className="hidden xl:block text-[10px] font-extrabold uppercase tracking-widest text-[#E11D2A] leading-tight border-l border-[#273042] pl-2.5">Muri sports,<br/>ni igitego!</span>
        </Link>
        <LiveFootballBtn />

        <nav className="hidden lg:flex items-center gap-0.5">
          {LINKS.map(([label, to, sub]) => sub ? <NavMenu key={to} items={sub} /> : (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1).replace(/\//g, "-")}`}
              className={({ isActive }) => `px-2.5 xl:px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeFor(to, isActive) ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
              {label}
            </NavLink>
          ))}
          <NavMenu items={MORE} label="More" id="more" />
        </nav>

        <div className="flex items-center gap-2">
          {isAuthed && !isSubscribed && (
            <button onClick={() => window.dispatchEvent(new CustomEvent("open-subscribe"))} data-testid="header-unlock-btn"
              className="hidden sm:flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-sm font-extrabold uppercase btn-glow transition-colors">
              <Lock className="w-4 h-4" /> Unlock
            </button>
          )}
          {isAuthed ? (
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} data-testid="account-menu-btn" aria-label="Account menu" aria-haspopup="menu" aria-expanded={menuOpen}
                className="flex items-center gap-2 h-10 pl-2 pr-3 rounded-full bg-[#121620] border border-[#273042] hover:border-[#E11D2A]/60 transition-colors">
                <span className="w-7 h-7 rounded-full bg-[#E11D2A] flex items-center justify-center text-white text-xs font-bold">
                  {(user?.displayName || user?.phone || "U").slice(-2)}
                </span>
                {isSubscribed && <Crown className="w-4 h-4 text-[#2563EB]" />}
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-52 rounded-xl bg-[#121620] border border-[#273042] shadow-2xl z-20 overflow-hidden py-1">
                    <div className="px-4 py-3 border-b border-[#273042]">
                      <div className="text-sm text-white font-semibold truncate">{user?.displayName || user?.phone}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{isSubscribed ? `${user?.tier} member` : "Free account"}</div>
                    </div>
                    <button onClick={() => { setMenuOpen(false); nav("/profile"); }} data-testid="menu-profile-btn" className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-white/5"><User className="w-4 h-4" /> My Account</button>
                    {isBroadcaster(user) && <button onClick={() => { setMenuOpen(false); nav("/broadcast"); }} data-testid="menu-broadcast-btn" className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-white/5"><Radio className="w-4 h-4" /> Go live (broadcaster)</button>}
                    {(isAdmin || isStaff) && <button onClick={() => { setMenuOpen(false); nav("/admin"); }} data-testid="menu-admin-btn" className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-200 hover:bg-white/5"><LayoutDashboard className="w-4 h-4" /> {isAdmin ? "Admin Portal" : "Staff Dashboard"}</button>}
                    <button onClick={() => { setMenuOpen(false); logout(); nav("/"); }} data-testid="menu-logout-btn" className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-white/5"><LogOut className="w-4 h-4" /> Sign out</button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button onClick={openAuth} data-testid="header-login-btn"
              className="h-10 px-5 whitespace-nowrap rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-sm font-bold btn-glow transition-colors">
              Sign in
            </button>
          )}
          <button onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-white/5">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#273042] bg-[#0A0C10] px-4 py-3 space-y-1">
          {[...LINKS, ...MORE].map(([label, to, sub]) => (
            <div key={to}>
              <NavLink to={to} end={!!sub} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${to.slice(1).replace(/\//g, "-")}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${activeFor(to, isActive) ? "text-white bg-white/5" : "text-slate-300"}`}>
                <Radio className="w-4 h-4 text-[#E11D2A]" /> {label}
              </NavLink>
              {sub && <div className="ml-7 border-l border-[#273042] pl-2">{sub.slice(1).map(([l, t]) => (
                <NavLink key={t} to={t} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-football-${t.slice(1).replace(/\//g, "-")}`}
                  className={({ isActive }) => `block px-3 py-2 rounded-lg text-sm ${isActive ? "text-white bg-white/5" : "text-slate-400"}`}>{l}</NavLink>))}</div>}
            </div>
          ))}
          {isAuthed && !isSubscribed && (
            <button onClick={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent("open-subscribe")); }} data-testid="mobile-unlock-btn"
              className="w-full mt-1 flex items-center justify-center gap-1.5 h-11 rounded-lg bg-[#E11D2A] text-white text-sm font-extrabold uppercase btn-glow">
              <Lock className="w-4 h-4" /> Unlock everything
            </button>
          )}
          <div className="mt-2 pt-2 border-t border-[#273042] flex flex-col gap-1">
            <button onClick={() => { setMobileOpen(false); window.dispatchEvent(new Event("bbk-open-install")); }} data-testid="mobile-install-btn"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5">
              <Download className="w-4 h-4 text-[#E11D2A]" /> Install app
            </button>
            {!isAuthed && (
              <button onClick={() => { setMobileOpen(false); setStaffOpen(true); }} data-testid="mobile-staff-login-btn"
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/5">
                <ShieldCheck className="w-3.5 h-3.5" /> Staff login
              </button>
            )}
          </div>
        </div>
      )}
      <StaffLoginModal open={staffOpen} onClose={() => setStaffOpen(false)} />
    </header>
  );
};
